import React, { useState, useEffect } from 'react';
import { ArrowRight, Trophy, TrendingUp, Flame } from 'lucide-react';
import { supabase, getLessonsTable } from '../utils/supabase';
import { type LanguageLevel } from '../types';
import { useTranslation } from '../utils/translations';
import { EndOfLessonSurvey } from './EndOfLessonSurvey';
import { SentenceStatsVisualization } from './SentenceStatsVisualization';
import { calculateStreak, type StreakData } from '../utils/streak';
import { sendPushNotification, checkShouldSendNotification } from '../utils/pushNotifications';
import { playLessonCompleteSound } from '../utils/audio';

interface LessonCompletionPopupProps {
  currentLessonTitle: string;
  currentLevel: LanguageLevel;
  userId: string;
  lessonId: string;
  initialUnderstanding: number | null;
  surveyId: string | null;
  onContinueToNext: (nextLessonId: string) => void;
  onGoToLessons: () => void;
  nativeLanguage?: string;
}

export function LessonCompletionPopup({
  currentLessonTitle,
  currentLevel,
  userId,
  lessonId,
  initialUnderstanding,
  surveyId,
  onContinueToNext,
  onGoToLessons,
  nativeLanguage
}: LessonCompletionPopupProps) {
  const [nextLesson, setNextLesson] = useState<{ id: string; title: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSurvey, setShowSurvey] = useState(true);
  const [finalUnderstanding, setFinalUnderstanding] = useState<number | null>(null);
  const [improvement, setImprovement] = useState<number | null>(null);
  const [actualInitialUnderstanding, setActualInitialUnderstanding] = useState<number | null>(initialUnderstanding);
  const [sentenceStats, setSentenceStats] = useState<{
    totalSentences: number;
    averageAccuracy: number;
    perfectSentences: number;
    goodSentences: number;
    needsPractice: number;
    sentenceDetails: Array<{ index: number; percentage: number }>;
  } | null>(null);
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const { t, isRTL } = useTranslation(nativeLanguage);

  console.log('LessonCompletionPopup mounted with:', {
    initialUnderstanding,
    actualInitialUnderstanding,
    surveyId,
    showSurvey: true,
    lessonId
  });

  // Update actualInitialUnderstanding if initialUnderstanding prop changes
  useEffect(() => {
    if (initialUnderstanding !== null && initialUnderstanding !== actualInitialUnderstanding) {
      console.log('Updating actualInitialUnderstanding from prop:', initialUnderstanding);
      setActualInitialUnderstanding(initialUnderstanding);
    }
  }, [initialUnderstanding]);

  // Load survey data on mount if not provided
  useEffect(() => {
    const loadSurveyIfNeeded = async () => {
      // If we don't have initial understanding and we have a surveyId, fetch it
      if (actualInitialUnderstanding === null && surveyId) {
        console.log('Loading survey data for surveyId:', surveyId);
        const { data: survey } = await supabase
          .from('lesson_understanding_surveys')
          .select('initial_understanding')
          .eq('id', surveyId)
          .single();

        if (survey && survey.initial_understanding !== null) {
          console.log('Loaded initial understanding from surveyId:', survey.initial_understanding);
          setActualInitialUnderstanding(survey.initial_understanding);
        }
      }
      // If we don't have initial understanding and no surveyId, search by lessonId
      else if (actualInitialUnderstanding === null && !surveyId) {
        console.log('Searching for existing survey by lessonId:', lessonId);
        const { data: survey } = await supabase
          .from('lesson_understanding_surveys')
          .select('id, initial_understanding')
          .eq('user_id', userId)
          .eq('lesson_id', lessonId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (survey && survey.initial_understanding !== null) {
          console.log('Found existing survey with initial understanding:', survey.initial_understanding);
          setActualInitialUnderstanding(survey.initial_understanding);
        }
      }
    };

    loadSurveyIfNeeded();
  }, [surveyId, lessonId, userId]);

  // Fetch sentence practice stats
  const fetchSentenceStats = async () => {
    try {
      console.log('Fetching sentence stats for lesson:', lessonId);
      const { data: stats, error } = await supabase
        .from('sentence_practice_stats')
        .select('sentence_index, max_match_percentage')
        .eq('user_id', userId)
        .eq('lesson_id', lessonId)
        .order('sentence_index', { ascending: true });

      if (error) throw error;

      if (stats && stats.length > 0) {
        const sentenceDetails = stats.map(s => ({
          index: s.sentence_index,
          percentage: s.max_match_percentage
        }));

        const totalSentences = stats.length;
        const averageAccuracy = stats.reduce((sum, s) => sum + s.max_match_percentage, 0) / totalSentences;
        const perfectSentences = stats.filter(s => s.max_match_percentage === 100).length;
        const goodSentences = stats.filter(s => s.max_match_percentage >= 75 && s.max_match_percentage < 100).length;
        const needsPractice = stats.filter(s => s.max_match_percentage < 75).length;

        setSentenceStats({
          totalSentences,
          averageAccuracy,
          perfectSentences,
          goodSentences,
          needsPractice,
          sentenceDetails
        });

        console.log('Sentence stats loaded:', {
          totalSentences,
          averageAccuracy,
          perfectSentences
        });
      } else {
        console.log('No sentence stats found for this lesson');
      }
    } catch (error) {
      console.error('Error fetching sentence stats:', error);
    }
  };

  const handleFinalSurveySubmit = async (finalPercentage: number) => {
    console.log('handleFinalSurveySubmit called with:', finalPercentage);

    let currentSurveyId = surveyId;
    let currentInitialUnderstanding = actualInitialUnderstanding;

    // Set improvement immediately for better UX
    const improvementValue = finalPercentage - (currentInitialUnderstanding ?? 50);
    setFinalUnderstanding(finalPercentage);
    setImprovement(improvementValue);

    // Hide survey immediately
    setShowSurvey(false);

    // Do all async operations in background (non-blocking)
    (async () => {
      try {
        // If no survey exists, find or create one
        if (!currentSurveyId) {
          console.log('No survey ID, looking for existing survey...');
          const { data: existingSurvey } = await supabase
            .from('lesson_understanding_surveys')
            .select('id, initial_understanding')
            .eq('user_id', userId)
            .eq('lesson_id', lessonId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (existingSurvey) {
            console.log('Found existing survey:', existingSurvey);
            currentSurveyId = existingSurvey.id;
            currentInitialUnderstanding = existingSurvey.initial_understanding;
            setActualInitialUnderstanding(existingSurvey.initial_understanding);
          } else {
            console.log('No existing survey found, creating new survey with 50% initial understanding');
            const { data: newSurvey, error: createError } = await supabase
              .from('lesson_understanding_surveys')
              .insert({
                user_id: userId,
                lesson_id: lessonId,
                initial_understanding: 50
              })
              .select()
              .single();

            if (createError) {
              console.error('Error creating survey:', createError);
              throw createError;
            }

            console.log('New survey created:', newSurvey);
            currentSurveyId = newSurvey.id;
            currentInitialUnderstanding = 50;
            setActualInitialUnderstanding(50);
          }
        }

        // Update with final understanding
        if (currentSurveyId) {
          console.log('Updating survey with final understanding');
          const { error } = await supabase
            .from('lesson_understanding_surveys')
            .update({
              final_understanding: finalPercentage,
              completed_at: new Date().toISOString()
            })
            .eq('id', currentSurveyId);

          if (error) {
            console.error('Error updating survey:', error);
          } else {
            console.log('Survey updated successfully');
          }
        }

        // Fetch sentence stats after survey is saved
        await fetchSentenceStats();
      } catch (error) {
        console.error('Error in survey submission:', error);
      }
    })();
  };

  useEffect(() => {
    playLessonCompleteSound();
  }, []);

  useEffect(() => {
    // Trigger confetti animation
    const confettiCount = 50;
    const confettiContainer = document.createElement('div');
    confettiContainer.style.position = 'fixed';
    confettiContainer.style.top = '0';
    confettiContainer.style.left = '0';
    confettiContainer.style.width = '100%';
    confettiContainer.style.height = '100%';
    confettiContainer.style.pointerEvents = 'none';
    confettiContainer.style.zIndex = '1000';
    document.body.appendChild(confettiContainer);

    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti-piece';
      confetti.style.left = Math.random() * 100 + '%';
      confetti.style.animationDelay = Math.random() * 0.5 + 's';
      confetti.style.animationDuration = (Math.random() * 2 + 2) + 's';
      confettiContainer.appendChild(confetti);
    }

    // Clean up confetti after animation
    const confettiTimeout = setTimeout(() => {
      if (document.body.contains(confettiContainer)) {
        document.body.removeChild(confettiContainer);
      }
    }, 4000);

    // Find next lesson
    const findNextLesson = async () => {
      try {
        if (currentLevel === 'Custom') {
          setLoading(false);
          return;
        }

        const lessonsTable = getLessonsTable();

        // Get current lesson to find its order
        const { data: currentLesson } = await supabase
          .from(lessonsTable)
          .select('order_index')
          .eq('level', currentLevel)
          .eq('title', currentLessonTitle)
          .single();

        if (currentLesson) {
          // Find next lesson
          const { data: nextLessonData } = await supabase
            .from(lessonsTable)
            .select('id, title')
            .eq('level', currentLevel)
            .eq('is_review', false)
            .gt('order_index', currentLesson.order_index)
            .order('order_index', { ascending: true })
            .limit(1)
            .maybeSingle();

          setNextLesson(nextLessonData);
        }
      } catch (error) {
        console.error('Error finding next lesson:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadStreakData = async () => {
      const streak = await calculateStreak(userId, nativeLanguage || 'English');
      setStreakData(streak);
    };

    const sendLessonCompletionNotification = async () => {
      const shouldSend = await checkShouldSendNotification(userId, 'lesson_completion');
      if (shouldSend) {
        await sendPushNotification(
          userId,
          'Lesson Completed!',
          `Great job completing "${currentLessonTitle}"!`
        );
      }
    };

    const sendStreakNotification = async () => {
      const streak = await calculateStreak(userId, nativeLanguage || 'English');
      const shouldSend = await checkShouldSendNotification(userId, 'streak_milestone');

      if (shouldSend && streak.currentStreak > 0) {
        const milestones = [1, 7, 14, 30, 60, 100, 365];
        if (milestones.includes(streak.currentStreak)) {
          let message = '';
          if (streak.currentStreak === 1) {
            message = 'You started your first streak!';
          } else if (streak.currentStreak === 7) {
            message = 'One week of consistency! Keep it up!';
          } else if (streak.currentStreak === 30) {
            message = 'A whole month of learning! Amazing!';
          } else if (streak.currentStreak === 100) {
            message = '100 days! You\'re unstoppable!';
          } else {
            message = `You\'ve reached a ${streak.currentStreak}-day streak!`;
          }

          await sendPushNotification(
            userId,
            `${streak.currentStreak} Day Streak!`,
            message
          );
        }
      }
    };

    findNextLesson();
    loadStreakData();
    sendLessonCompletionNotification();
    sendStreakNotification();

    return () => {
      clearTimeout(confettiTimeout);
      if (document.body.contains(confettiContainer)) {
        document.body.removeChild(confettiContainer);
      }
    };
  }, [currentLessonTitle, currentLevel, userId, lessonId]);

  if (showSurvey) {
    console.log('Rendering EndOfLessonSurvey with actualInitialUnderstanding:', actualInitialUnderstanding);
    return (
      <EndOfLessonSurvey
        initialUnderstanding={actualInitialUnderstanding ?? 50}
        onSubmit={handleFinalSurveySubmit}
        nativeLanguage={nativeLanguage}
      />
    );
  }

  console.log('Rendering stats page with:', {
    improvement,
    sentenceStats: sentenceStats ? 'loaded' : 'null',
    loading,
    nextLesson: nextLesson ? 'found' : 'null'
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full my-8 flex flex-col max-h-[90vh]" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Scrollable content area */}
        <div className="overflow-y-auto flex-1 p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4 animate-celebration-bounce">
              <Trophy className="h-8 w-8 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('🎉 Congratulations!')}</h2>
            <p className="text-gray-600">{t('You\'ve completed the lesson')}</p>
            <p className="font-medium text-indigo-600 mt-1">"{currentLessonTitle}"</p>
          </div>

          {improvement !== null && (
            <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
              <div className={`flex items-center justify-center mb-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                <TrendingUp className={`h-6 w-6 text-green-600 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <p className="text-sm font-medium text-gray-700">{t('Your improvement')}</p>
              </div>
              <div className="flex items-center justify-center">
                <span className="text-3xl font-bold text-green-600">
                  {improvement > 0 ? '+' : ''}{improvement}%
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {improvement > 20 ? t('Excellent work!') : improvement > 0 ? t('Great progress!') : t('Keep it up!')}
              </p>
            </div>
          )}

          {streakData && streakData.currentStreak > 0 && (
            <div className="mb-6 p-4 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl text-white">
              <div className="flex items-center justify-center gap-3">
                <div className="flex items-center justify-center w-12 h-12 bg-white/20 rounded-full">
                  <Flame className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-2xl font-bold">{streakData.currentStreak} Day Streak!</p>
                  <p className="text-white/80 text-sm">
                    {streakData.currentStreak === 1
                      ? "You started a streak!"
                      : streakData.currentStreak < 7
                      ? "Keep the momentum going!"
                      : streakData.currentStreak < 30
                      ? "Amazing dedication!"
                      : "You're on fire!"}
                  </p>
                </div>
              </div>
              {streakData.longestStreak > streakData.currentStreak && (
                <p className="text-white/70 text-xs mt-2 text-center">
                  Best: {streakData.longestStreak} days
                </p>
              )}
            </div>
          )}

          {sentenceStats && (
            <div className="mb-6">
              <SentenceStatsVisualization stats={sentenceStats} nativeLanguage={nativeLanguage} />
            </div>
          )}

          {loading ? (
            <div className="flex justify-center mb-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
            </div>
          ) : nextLesson ? (
            <div className="mb-6 p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700 mb-2">{t('Next lesson unlocked:')}</p>
              <p className="font-medium text-green-800">"{nextLesson.title}"</p>
            </div>
          ) : (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">{t('Great job! You\'ve completed all available lessons for this level.')}</p>
            </div>
          )}
        </div>

        {/* Fixed buttons at bottom */}
        <div className="border-t border-gray-200 p-6 bg-white rounded-b-2xl space-y-3">
          {nextLesson && (
            <button
              onClick={() => onContinueToNext(nextLesson.id)}
              className="w-full flex items-center justify-center px-6 py-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors font-medium"
            >
              {t('Continue to Next Lesson')}
              <ArrowRight className="h-5 w-5 ml-2" />
            </button>
          )}

          <button
            onClick={onGoToLessons}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
          >
            {t('Back to Lessons')}
          </button>
        </div>
      </div>
    </div>
  );
}